import {store} from "../store";
import VouchersProvider from "../providers/VouchersProvider";
import appConfig from "../appconfig";
import {getMilestoneRewardInterval, hasCustomDiscountOption, matchesSunsetPlusTier, shouldRuleBeAppliedToShop} from "../domains2/priceRules/priceRulesHelpers";
import {evaluateCustomerType} from "../libraries/utils";
import {
	ADD_FRIEND_INVITE_CODE_TO_ORDER,
	ADD_VOUCHER_TO_ORDER,
	CLEAR_VOUCHERS_FROM_ORDER,
	RECALCULATE_SUBSCRIPTION_SET_CART_ORDER_TAGS,
	SET_VOUCHER_RESERVATION_TIME,
	SET_VOUCHER_VIRTUAL_ORDER_ID
} from "../store/mutation-types";
import {CUSTOMER_TYPES} from "../domains2/plugins/commonTypes/CommonTypes";
import {DateTime} from "luxon";
import { PriceRuleCustomFilters } from "../domains2/plugins/commonTypes/PriceRulesTypes";

const TimeBox = appConfig.timeBoxImpl.timebox;
const TimeBoxFormats = appConfig.timeBoxImpl.Formats;

export const voucherValidationHelper = async (self, voucherCodeOrId, skipReservation = false, label = "") => {
	const {customerDetails, globalSelectedShop, isSunsetPointsMember} = store.getters;
	const input = {
		success: true,
		message: self.$t("utilityComponents.voucherInput.voucherActive")
	};
	try {
		if (voucherCodeOrId === ((customerDetails || {}).meta || {}).friendInviteCode) {
			if (isChannelSupported()) {
				input.message = self.$t("utilityComponents.voucherInput.friendInviteSunsetPointBadCode");
			} else {
				input.message = self.$t("utilityComponents.voucherInput.friendInviteChannelBad");
			}
			store.commit(ADD_FRIEND_INVITE_CODE_TO_ORDER, undefined);
			input.success = false;
		} else {
			const voucherData = await VouchersProvider.redeemVoucher(
				voucherCodeOrId,
				globalSelectedShop.id,
				skipReservation,
				label
			);
			// we need this !store.getters.voucherVirtualOrderId because voucherVirtualOrderId is set for the mystery box
			// just before this step
			if(voucherData && voucherData.voucherVirtualOrderId && !store.getters.voucherVirtualOrderId) {
				store.commit(SET_VOUCHER_VIRTUAL_ORDER_ID, voucherData.voucherVirtualOrderId);
			}
			if(voucherData && voucherData.voucherReservationTimeInMinutes) {
				store.commit(SET_VOUCHER_RESERVATION_TIME, {
					voucherId: voucherData.voucherId,
					voucherReservationTimeInMinutes: voucherData.voucherReservationTimeInMinutes,
					shopTimeZone: globalSelectedShop.shopTimeZone
				});
			}
			if (voucherData && voucherData.pleaseLoginToUseFriendInviteCode) {
				if (isChannelSupported()) {
					input.message = self.$t("utilityComponents.voucherInput.friendInviteGuest");
				} else {
					input.message = self.$t("utilityComponents.voucherInput.friendInviteChannelBad");
				}
				store.commit(ADD_FRIEND_INVITE_CODE_TO_ORDER, undefined);
				input.success = false;
			} else if (voucherData && voucherData.itIsNotUsersFirstOrder) {
				if (isChannelSupported()) {
					input.message = self.$t("utilityComponents.voucherInput.friendInviteNotFirstOrder");
				} else {
					input.message = self.$t("utilityComponents.voucherInput.friendInviteChannelBad");
				}
				store.commit(ADD_FRIEND_INVITE_CODE_TO_ORDER, undefined);
				input.success = false;
			}
			else if (voucherData && voucherData.isFriendInviteCodeValid) {
				if (!isChannelSupported()) {
					input.success = false;
					input.message = self.$t("utilityComponents.voucherInput.friendInviteChannelBad");
					store.commit(ADD_FRIEND_INVITE_CODE_TO_ORDER, undefined);
				} else if (isSunsetPointsMember) {
					input.message = self.$t("utilityComponents.voucherInput.friendInviteVoucherActive").replace(/{{\s?pointsToGrant\s?}}/gi, customerDetails.sunsetPoints.friendInvite.pointsToGrant);
					store.commit(ADD_FRIEND_INVITE_CODE_TO_ORDER, voucherCodeOrId);
				} else {
					input.success = false;
					input.message = self.$t("utilityComponents.voucherInput.friendInviteGuest");
					store.commit(ADD_FRIEND_INVITE_CODE_TO_ORDER, undefined);
				}
			} else if(voucherData && voucherData.voucherId) {
				// ensure that only last valid voucher is applied and stored
				await store.dispatch(CLEAR_VOUCHERS_FROM_ORDER);
				await store.dispatch(ADD_VOUCHER_TO_ORDER, voucherData);
				await store.dispatch(RECALCULATE_SUBSCRIPTION_SET_CART_ORDER_TAGS);
			} else {
				store.commit(ADD_FRIEND_INVITE_CODE_TO_ORDER, undefined);
				input.success = false;
				input.message = self.$t("utilityComponents.voucherInput.missingModel");
			}
		}
	} catch(e) {
		store.commit(ADD_FRIEND_INVITE_CODE_TO_ORDER, undefined);
		// we need to preserve the original error message key for further handling
		const messageKey = mapError(self, e);
		input.message = self.$t(messageKey);
		input.messageKey = messageKey;
		input.success = false;
	}
	return input;
};

const mapError = (self, error) => {
	switch(error.response.data.message){
	case "voucher-redemption-rules-model-missing":
		return "utilityComponents.voucherInput.missingRedemptionRule";
	case "expired-voucher":
		return "utilityComponents.voucherInput.expired";
	case "exceeded-max-usages":
		return "utilityComponents.voucherInput.maxUsages";
	case "missing-price-rule":
		return "utilityComponents.voucherInput.missingPriceRule";
	case "voucher-model-missing":
		return "utilityComponents.voucherInput.missingModel";
	case "invalid-shop":
		return "utilityComponents.voucherInput.invalidShop";
	case "voucher-not-applicable-for-products-in-cart":
		return "utilityComponents.voucherInput.voucherNotApplicableForProductsInCart";
	case "daily-spending-limit-reached-for-free-meal-voucher":
		return "utilityComponents.voucherInput.dailySpendingLimitReachedForFreeMealVoucher";
	default:
		return "utilityComponents.voucherInput.error";
	}
};

const isChannelSupported = () => {
	const {envPrefix} = store.getters;
	if (envPrefix === "p") {
		return appConfig.channelAlias() === "mobileApp";
	} else {
		return ["mobileApp", "mobileBrowser"].includes(appConfig.channelAlias());
	}
};

export const hasVoucherReachedLimitOnThisDevice = (priceRuleId) => {
	const limitedDiscountAssignments = store.getters.limitedDiscountAssignments;
	if(limitedDiscountAssignments && limitedDiscountAssignments.length) {
		const voucherAssignments = limitedDiscountAssignments.filter(lda => lda.details.type === "voucher" && lda.details.id === priceRuleId.toString());
		if (!voucherAssignments.length) {
			return false;
		}

		const maxUsageOfVoucher = voucherAssignments && voucherAssignments.length ? voucherAssignments[0].details.maxUsage : 0;
		const totalUsageCount = voucherAssignments.reduce(
			(acc, curr) => acc + (curr.details.usageCount),
			0
		);
		return totalUsageCount >= maxUsageOfVoucher;
	}
	return false;
};

export const verifyVoucherAvailability = (voucher) => {
	if(!voucher || !voucher.isActive || voucher.usedAt) {
		return false;
	}

	const { options, shopPattern, expiryDate } = voucher;

	// is expired
	if(expiryDate) {
		const isExpired = TimeBox.fromISO(expiryDate).format(TimeBoxFormats.YYYY_MM_DD).exec() < TimeBox.now().format(TimeBoxFormats.YYYY_MM_DD).exec();
		if(isExpired) {
			return false;
		}
	}

	//	reached usage limits
	if(hasVoucherReachedLimitOnThisDevice(voucher.priceRuleId)) {
		return false;
	}
	if(voucher.maxNumberOfUsages && voucher.maxNumberOfUsages <= voucher.usageCounter) {
		return false;
	}

	// available by shops rules
	if(shopPattern && Array.isArray(shopPattern) && shopPattern.length && !store.getters.globalSelectedShop) {
		return false;
	}
	if(shopPattern && typeof shopPattern === "object" && !shopPattern.isWhitelist && shopPattern.list && shopPattern.list.length && !store.getters.globalSelectedShop) {
		return false;
	}
	if (shopPattern && store.getters.globalSelectedShop) {
		if (!shouldRuleBeAppliedToShop(shopPattern, store.getters.globalSelectedShop.id)) {
			return false;
		}
	}

	// is available on channel
	if(options && options.channels && options.channels.length && !options.channels.includes(appConfig.channelAlias())) {
		return false;
	}

	// is available on delivery method
	if(options && options.deliveryMethods && options.deliveryMethods.length) {
		const deliveryMethod = store.getters.tenantSpecificData && store.getters.tenantSpecificData.deliveryChoice;
		if(deliveryMethod && !options.deliveryMethods.includes(deliveryMethod)) {
			return false;
		}
	}

	const customerType = evaluateCustomerType();
	// is available for customer type
	if(options && options.customerTypes && options.customerTypes.length) {
		if(customerType && !options.customerTypes.includes(customerType)) {
			return false;
		}
	}

	// check milestone reward voucher "flag" and remove it from available vouchers
	// as those are handled separately in the dedicated milestone reward logic/UI
	if (customerType === CUSTOMER_TYPES.PLUS_MEMBERS) {
		const isMilestoneRewardVoucher = Number.isFinite(getMilestoneRewardInterval(options));
		if(isMilestoneRewardVoucher) {
			return false;
		}
	}

	if(hasCustomDiscountOption(options, PriceRuleCustomFilters.freeMeal)) {
		const subscriptionDetails = store.getters.subscriptionDetails && store.getters.subscriptionDetails;
		if(!subscriptionDetails) {
			return false;
		}
		const sunsetPlusApplicablePriceRule = (store.getters.priceRules || [])
			.find(pr => matchesSunsetPlusTier(pr.options, subscriptionDetails.subscriptionPlanName));
		const discountMax = sunsetPlusApplicablePriceRule && sunsetPlusApplicablePriceRule.options && sunsetPlusApplicablePriceRule.options.discountMax;
		const currentDate = DateTime.now().toISODate();
		const todaySpending = subscriptionDetails.meta && subscriptionDetails.meta.dailySpending && subscriptionDetails.meta.dailySpending[currentDate];
		if(discountMax && todaySpending && todaySpending >= discountMax) {
			return false;
		}
	}

	// check if voucher has related price rule loaded - if not, consider it invalid
	const priceRules = (store.getters.productCatalog && store.getters.productCatalog.priceRules) || [];
	const relatedPriceRule = priceRules.find(pr => +pr.id === +voucher.priceRuleId);
	if (!relatedPriceRule) {
		return false;
	}

	return true;
};
